import { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { api } from '../lib/api';
import { fmtNumber, fmtPercent, fmtDelta, fmtSeconds } from '../lib/format';
import { MetricCard, SectionHeader, EmptyState } from '../components/ui';
import { PROPERTIES } from '../../../shared/types';
import NewsletterPanel from './NewsletterPanel';

interface Props {
  property: string;
}

export default function AudienceSection({ property }: Props) {
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [publishedArticles, setPublishedArticles] = useState<any[]>([]);
  const [publishedWeek, setPublishedWeek] = useState<string | null>(null);
  const [instagram, setInstagram] = useState<any[]>([]);
  const [instagramAll, setInstagramAll] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.snapshot(property, 12),
      api.articlesPublishedPerformance(property),
      api.instagram(property),
      api.instagram('ALL'),
    ])
      .then(([s, a, i, iAll]) => {
        setSnapshots(s.snapshots);
        setPublishedArticles(a.articles);
        setPublishedWeek(a.weekEnding);
        setInstagram(i.instagram);
        setInstagramAll(iAll.instagram);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [property]);

  if (loading) {
    return (
      <div className="text-xs text-white/40 py-12 text-center">Loading audience data…</div>
    );
  }

  if (error) {
    return (
      <EmptyState
        title="Couldn't load audience data"
        message={`The Worker API returned: ${error}. Check that VITE_WORKER_URL is set correctly and the Worker is deployed.`}
      />
    );
  }

  const latest = snapshots[0];
  const previous = snapshots[1];

  const sessionsDelta =
    latest?.organic_sessions != null && previous?.organic_sessions != null
      ? ((latest.organic_sessions - previous.organic_sessions) / previous.organic_sessions) * 100
      : null;

  const postsDelta =
    latest?.posts_published != null && previous?.posts_published != null
      ? (latest.posts_published as number) - (previous.posts_published as number)
      : null;

  // For ALL, multiple IG rows share a week_ending (one per account); sum them.
  // For a specific property, there's at most one row per week.
  const igEngagementByWeek = new Map<string, number>();
  for (const r of instagram) {
    const wk = r.week_ending as string;
    igEngagementByWeek.set(
      wk,
      (igEngagementByWeek.get(wk) ?? 0) + ((r.total_engagement as number) ?? 0)
    );
  }

  const trendData = [...snapshots]
    .reverse()
    .map((s, idx) => ({
      week: `W${idx + 1}`,
      organic: s.organic_sessions,
      total: s.total_sessions,
      engagement: igEngagementByWeek.get(s.week_ending as string) ?? null,
    }));

  const latestIG = instagram[0];

  const propertyLabel = (id: string) => PROPERTIES.find((p) => p.id === id)?.label ?? id;
  const igLatestWeek = instagramAll[0]?.week_ending as string | undefined;
  const igThisWeek = igLatestWeek
    ? instagramAll
        .filter((r) => r.week_ending === igLatestWeek)
        .sort((a, b) => (b.total_reach ?? 0) - (a.total_reach ?? 0))
    : [];

  return (
    <div className="space-y-8">
      <div>
        <SectionHeader title="Traffic & SEO" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <MetricCard
            label="Total sessions"
            value={fmtNumber(latest?.total_sessions)}
            delta={fmtDelta(sessionsDelta)}
          />
          <MetricCard
            label="Organic sessions"
            value={fmtNumber(latest?.organic_sessions)}
            hint={latest?.total_sessions ? `${fmtPercent(latest.organic_sessions / latest.total_sessions)} of total` : undefined}
          />
          <MetricCard
            label="Posts published"
            value={fmtNumber(latest?.posts_published)}
            delta={
              postsDelta != null
                ? {
                    text: `${postsDelta > 0 ? '▲' : postsDelta < 0 ? '▼' : ''} ${Math.abs(postsDelta)} vs last week`,
                    direction:
                      postsDelta > 0 ? 'up' : postsDelta < 0 ? 'down' : 'neutral',
                  }
                : undefined
            }
            hint={
              property === 'HKA_SG' || property === 'HKA_TH'
                ? 'combined HKA total — per-region split coming later'
                : 'this week'
            }
          />
          <MetricCard
            label="Avg page rank"
            value={latest?.avg_page_rank != null ? latest.avg_page_rank.toFixed(1) : '—'}
            hint="weighted by traffic"
          />
          <MetricCard
            label="Keywords top 3"
            value={fmtNumber(latest?.keywords_top_3)}
            delta={
              latest?.keywords_top_3_delta != null
                ? {
                    text: `${latest.keywords_top_3_delta > 0 ? '▲' : latest.keywords_top_3_delta < 0 ? '▼' : ''} ${Math.abs(latest.keywords_top_3_delta)} new`,
                    direction:
                      latest.keywords_top_3_delta > 0
                        ? 'up'
                        : latest.keywords_top_3_delta < 0
                        ? 'down'
                        : 'neutral',
                  }
                : undefined
            }
          />
        </div>
      </div>

      {trendData.length >= 2 && (
        <div className="raised-card">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-sm font-medium">12-week traffic & engagement trend</div>
            <div className="text-xs text-white/40 flex gap-3">
              <span><span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1" />organic sessions</span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-pink-500 mr-1" />IG engagement</span>
            </div>
          </div>
          <div style={{ width: '100%', height: 180 }}>
            <ResponsiveContainer>
              <LineChart data={trendData}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="week"
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
                />
                <Tooltip
                  contentStyle={{
                    background: '#161616',
                    border: '1px solid rgba(255,255,255,0.16)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="organic"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="engagement"
                  stroke="#ec4899"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="raised-card">
        <SectionHeader
          title="Recently published articles"
          hint={
            publishedWeek
              ? `newest first — performance from week ending ${publishedWeek} where available (GA4 + GSC)`
              : 'awaiting RSS data'
          }
        />
        <div className="text-xs text-white/40 -mt-2 mb-4 leading-relaxed">
          Note: WordPress's <code>pubDate</code> reflects the latest publish-or-update timestamp.
          Genuinely new articles (published this week) won't have sessions yet for the dashboard
          week — they didn't exist during the measurement window. Evergreen articles that were
          recently refreshed will show their accumulated traffic. Numbers fill in fully a week
          after publishing.
        </div>
        {publishedArticles.length === 0 ? (
          <div className="text-xs text-white/40 py-4">
            No articles captured yet for this property. RSS source needs to have run at least once.
            Other properties besides HC_SG aren't wired into GA4 yet — performance columns will be
            blank until they are.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 text-xs border-b border-border-subtle">
                <th className="text-left font-normal pb-2 w-2/5">Article</th>
                <th className="text-left font-normal pb-2">Published</th>
                <th className="text-right font-normal pb-2">Sessions</th>
                <th className="text-right font-normal pb-2">Avg engagement</th>
                <th className="text-right font-normal pb-2">GSC clicks</th>
                <th className="text-right font-normal pb-2">GSC pos</th>
              </tr>
            </thead>
            <tbody>
              {publishedArticles.slice(0, 15).map((a, i) => (
                <tr key={i} className="border-b border-border-subtle/50 last:border-0">
                  <td className="py-2.5 truncate max-w-0">
                    <a
                      href={a.url as string}
                      target="_blank"
                      rel="noopener"
                      className="hover:text-blue-400 transition-colors"
                    >
                      {(a.title as string) || (a.url as string)}
                    </a>
                  </td>
                  <td className="py-2.5 text-white/60">
                    {(a.publish_date as string) || '—'}
                  </td>
                  <td className="text-right py-2.5">
                    {a.sessions != null ? fmtNumber(a.sessions as number) : '—'}
                  </td>
                  <td className="text-right py-2.5 text-white/60">
                    {a.avg_engagement_seconds != null
                      ? fmtSeconds(a.avg_engagement_seconds as number)
                      : '—'}
                  </td>
                  <td className="text-right py-2.5">
                    {a.gsc_clicks != null ? fmtNumber(a.gsc_clicks as number) : '—'}
                  </td>
                  <td className="text-right py-2.5 text-white/60">
                    {a.gsc_avg_position != null
                      ? (a.gsc_avg_position as number).toFixed(1)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="raised-card">
        <SectionHeader
          title="Instagram — weekly snapshot"
          hint={igLatestWeek ? `week ending ${igLatestWeek}` : 'awaiting data'}
        />
        {igThisWeek.length === 0 ? (
          <div className="text-xs text-white/40 py-4">No Instagram data yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/40 text-xs border-b border-border-subtle">
                <th className="text-left font-normal pb-2">Account</th>
                <th className="text-right font-normal pb-2">Posts</th>
                <th className="text-right font-normal pb-2">Reach</th>
                <th className="text-right font-normal pb-2">Impressions</th>
                <th className="text-right font-normal pb-2">Followers</th>
                <th className="text-right font-normal pb-2">Top post</th>
              </tr>
            </thead>
            <tbody>
              {igThisWeek.map((r) => (
                <tr key={r.property as string} className="border-b border-border-subtle/50 last:border-0">
                  <td className="py-2.5">{propertyLabel(r.property as string)}</td>
                  <td className="text-right py-2.5">{fmtNumber(r.posts_published as number)}</td>
                  <td className="text-right py-2.5">{fmtNumber(r.total_reach as number)}</td>
                  <td className="text-right py-2.5">{fmtNumber(r.total_impressions as number)}</td>
                  <td className="text-right py-2.5">
                    {fmtNumber(r.follower_count as number)}
                    {r.follower_delta != null && (r.follower_delta as number) !== 0 && (
                      <span
                        className={
                          (r.follower_delta as number) > 0 ? 'delta-up ml-1.5' : 'delta-down ml-1.5'
                        }
                      >
                        {(r.follower_delta as number) > 0 ? '▲' : '▼'}{' '}
                        {fmtNumber(Math.abs(r.follower_delta as number))}
                      </span>
                    )}
                  </td>
                  <td className="text-right py-2.5">
                    {r.top_post_url ? (
                      <a
                        href={r.top_post_url as string}
                        target="_blank"
                        rel="noopener"
                        className="hover:text-blue-400 transition-colors"
                      >
                        {fmtNumber(r.top_post_engagement as number)} eng
                      </a>
                    ) : (
                      <span className="text-white/40">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="raised-card">
        <SectionHeader title="Instagram" hint={latestIG ? 'last 7 days' : 'awaiting data'} />
        {latestIG ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-xs text-white/50">Reach</div>
              <div className="text-xl font-medium">{fmtNumber(latestIG.total_reach)}</div>
            </div>
            <div>
              <div className="text-xs text-white/50">Posts</div>
              <div className="text-xl font-medium">{fmtNumber(latestIG.posts_published)}</div>
            </div>
            <div>
              <div className="text-xs text-white/50">Followers</div>
              <div className="text-xl font-medium">{fmtNumber(latestIG.follower_count)}</div>
              <div
                className={
                  (latestIG.follower_delta ?? 0) >= 0 ? 'delta-up' : 'delta-down'
                }
              >
                {(latestIG.follower_delta ?? 0) >= 0 ? '▲' : '▼'}{' '}
                {fmtNumber(Math.abs(latestIG.follower_delta ?? 0))} this week
              </div>
            </div>
            <div>
              <div className="text-xs text-white/50">Engagement</div>
              <div className="text-xl font-medium">{fmtNumber(latestIG.total_engagement)}</div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-white/40">
            Connect Instagram Business accounts in <code className="text-white/60">worker/src/sources/meta.ts</code>.
          </div>
        )}
      </div>

      <NewsletterPanel />
    </div>
  );
}
